<template>
  <div class="location-filters">
    <!-- Location Search -->
    <div class="filter-group">
      <div class="filter-label">{{ $t('Search Location') }}</div>
      <div class="custom-location-input">
        <input
          id="location-search"
          ref="locationSearchInput"
          v-model="locationSearchValue"
          type="text"
          :placeholder="$t('Enter an address or landmark')"
          class="location-search-input"
          @input="onLocationInput"
          @focus="onLocationFocus"
          @blur="onLocationBlur"
        />
        <div v-if="showLocationSuggestions && locationSuggestions.length > 0" class="location-suggestions">
          <div
            v-for="(suggestion, index) in locationSuggestions"
            :key="`suggestion-${index}`"
            class="location-suggestion"
            @click="selectLocationSuggestion(suggestion)"
          >
            <span class="suggestion-icon">📍</span>
            <div class="suggestion-content">
              <div class="suggestion-name">{{ suggestion.description }}</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="selectedCustomLocation" class="selected-location">
        <span class="location-icon">📍</span>
        <div class="location-info">
          <div class="location-name">{{ selectedCustomLocation.description }}</div>
          <button @click="clearCustomLocation" class="clear-location-btn">{{ $t('Clear') }}</button>
        </div>
      </div>
    </div>

    <!-- Distance Selector (only shown when location is selected) -->
    <div v-if="selectedCustomLocation" class="filter-group">
      <div class="filter-label">{{ $t('Distance from location') }}</div>
      <div class="distance-options">
        <div
          v-for="band in distanceBands"
          :key="band.value"
          :class="[
            'distance-chip',
            { 'selected': selectedDistanceBand === band.value }
          ]"
          @click="selectDistanceBand(band.value)"
        >
          {{ band.label }}
        </div>
      </div>
    </div>

    <!-- Neighborhoods -->
    <div class="filter-group" v-if="availableNeighborhoods.length > 0">
      <div class="filter-label">{{ $t('Neighborhoods') }}</div>
      <div class="neighborhoods-list">
        <div
          v-for="neighborhood in availableNeighborhoods.slice(0, showAllNeighborhoods ? availableNeighborhoods.length : 6)"
          :key="neighborhood.name"
          class="neighborhood-option"
        >
          <input
            :id="`neighborhood-${neighborhood.name}`"
            type="checkbox"
            :value="neighborhood.name"
            :checked="selectedNeighborhoods.includes(neighborhood.name)"
            @change="toggleNeighborhood(neighborhood.name)"
            class="neighborhood-checkbox"
          />
          <label :for="`neighborhood-${neighborhood.name}`" class="neighborhood-label">
            {{ neighborhood.name }}
            <span class="hotel-count">({{ neighborhood.count }})</span>
          </label>
        </div>
        
        <button
          v-if="availableNeighborhoods.length > 6"
          @click="showAllNeighborhoods = !showAllNeighborhoods"
          class="toggle-neighborhoods"
        >
          {{ showAllNeighborhoods ? $t('Show less') : $t('Show more') }}
        </button>
      </div>
    </div>

    <!-- Distance Info -->
    <div v-if="selectedDistanceBand || selectedNeighborhoods.length > 0" class="filter-info">
      <div v-if="selectedDistanceBand" class="info-item">
        <span class="info-icon">📍</span>
        {{ getDistanceBandLabel(selectedDistanceBand) }}
      </div>
      <div v-if="selectedNeighborhoods.length > 0" class="info-item">
        <span class="info-icon">🏘️</span>
        {{ selectedNeighborhoods.length }} {{ $t('neighborhood(s) selected') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useSearchStore } from '~/store/search'

const emit = defineEmits(['update'])

const route = useRoute()
const searchStore = useSearchStore()

const selectedDistanceBand = ref<string>('')
const selectedNeighborhoods = ref<string[]>([])
const showAllNeighborhoods = ref(false)
const locationSearchInput = ref<HTMLInputElement>()
const locationSearchValue = ref<string>('')
const locationSuggestions = ref<any[]>([])
const showLocationSuggestions = ref<boolean>(false)
const selectedCustomLocation = ref<any>(null)

const distanceBands = [
  { value: '', label: 'Any distance' },
  { value: '1', label: 'Within 1 km' },
  { value: '2', label: 'Within 2 km' },
  { value: '5', label: 'Within 5 km' },
  { value: '10', label: 'Within 10 km' },
]

// Extract neighborhoods from hotel data
const availableNeighborhoods = computed(() => {
  const hotels = searchStore.hotelsProducts || []
  const neighborhoodMap = new Map<string, number>()
  
  hotels.forEach((hotel: any) => {
    // Try different location fields that might contain neighborhood info
    const locationFields = [
      hotel.neighborhood,
      hotel.district,
      hotel.area,
      hotel.location?.neighborhood,
      hotel.location?.district,
      hotel.address?.district,
      hotel.geo?.neighborhood
    ]
    
    locationFields.forEach(field => {
      if (field && typeof field === 'string' && field.trim()) {
        const neighborhood = field.trim()
        neighborhoodMap.set(neighborhood, (neighborhoodMap.get(neighborhood) || 0) + 1)
      }
    })
  })
  
  return Array.from(neighborhoodMap.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count) // Sort by hotel count descending
})

// Initialize Google Places API (new API)
const initGooglePlaces = () => {
  const googleMaps = (window as any).google
  if (googleMaps && googleMaps.maps && googleMaps.maps.places) {
    return true
  }
  return false
}

// Load Google Places API with async loading
const loadGooglePlacesAPI = () => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (initGooglePlaces()) {
      resolve(true)
      return
    }

    // Check if script is already in the document
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait a bit for it to load
      const checkInterval = setInterval(() => {
        if (initGooglePlaces()) {
          clearInterval(checkInterval)
          resolve(true)
        }
      }, 100)
      
      setTimeout(() => {
        clearInterval(checkInterval)
        console.warn('Google Places API timeout')
        resolve(false)
      }, 5000)
      return
    }

    // Load the Google Maps JavaScript API with loading=async
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyALIJJ5N1aLv1Utg6q-ivrscp0WIa0QCDE&libraries=places&loading=async`
    script.async = true
    script.defer = true
    script.onload = () => {
      if (initGooglePlaces()) {
        resolve(true)
      } else {
        resolve(false)
      }
    }
    script.onerror = () => {
      console.error('Failed to load Google Places API')
      resolve(false)
    }
    document.head.appendChild(script)
  })
}



// Location search functionality using new Google Places API
const onLocationInput = async () => {
  const query = locationSearchValue.value.trim()
  
  if (query.length < 2) {
    locationSuggestions.value = []
    showLocationSuggestions.value = false
    return
  }

  const googleMaps = (window as any).google
  if (googleMaps && googleMaps.maps && googleMaps.maps.places) {
    try {
      // Use new AutocompleteSuggestion API
      const request = {
        input: query,
        includedPrimaryTypes: ['geocode'],
        includedRegionCodes: [searchStore.selectedCity?.country_code || 'US']
      }
      
      const { suggestions } = await googleMaps.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
      
      if (suggestions && suggestions.length > 0) {
        // Convert new API format to match old format
        locationSuggestions.value = suggestions.map((suggestion: any) => ({
          description: suggestion.placePrediction.text.text,
          place_id: suggestion.placePrediction.placeId,
          structured_formatting: {
            main_text: suggestion.placePrediction.structuredFormat.mainText.text,
            secondary_text: suggestion.placePrediction.structuredFormat.secondaryText?.text || ''
          }
        }))
        showLocationSuggestions.value = true
      } else {
        locationSuggestions.value = []
        showLocationSuggestions.value = false
      }
    } catch (error) {
      console.warn('Google Places API error:', error)
      locationSuggestions.value = []
      showLocationSuggestions.value = false
    }
  } else {
    // Google Places API not loaded yet
    locationSuggestions.value = []
    showLocationSuggestions.value = false
  }
}

const onLocationFocus = () => {
  if (locationSuggestions.value.length > 0) {
    showLocationSuggestions.value = true
  }
}

const onLocationBlur = () => {
  // Delay hiding suggestions to allow for click events
  setTimeout(() => {
    showLocationSuggestions.value = false
  }, 200)
}

const selectLocationSuggestion = async (suggestion: any) => {
  locationSuggestions.value = []
  showLocationSuggestions.value = false
  
  // Get detailed place information including coordinates using new Place API
  const googleMaps = (window as any).google
  if (googleMaps && googleMaps.maps && googleMaps.maps.places && suggestion.place_id) {
    try {
      // Use new Place API
      const place = new googleMaps.maps.places.Place({
        id: suggestion.place_id
      })
      
      // Fetch place details
      await place.fetchFields({
        fields: ['displayName', 'location', 'formattedAddress', 'id']
      })
      
      if (place.location) {
        selectedCustomLocation.value = {
          description: place.formattedAddress || suggestion.description,
          place_id: suggestion.place_id,
          lat: place.location.lat(),
          lng: place.location.lng(),
          name: place.displayName
        }
        locationSearchValue.value = selectedCustomLocation.value.description
        emitUpdate()
      } else {
        console.warn('Failed to get place location')
        // Fallback to basic suggestion data
        selectedCustomLocation.value = {
          description: suggestion.description,
          place_id: suggestion.place_id
        }
        locationSearchValue.value = suggestion.description
        emitUpdate()
      }
    } catch (error) {
      console.warn('Error getting place details:', error)
      selectedCustomLocation.value = {
        description: suggestion.description,
        place_id: suggestion.place_id
      }
      locationSearchValue.value = suggestion.description
      emitUpdate()
    }
  } else {
    // No places API or place_id, use basic data
    selectedCustomLocation.value = {
      description: suggestion.description,
      place_id: suggestion.place_id
    }
    locationSearchValue.value = suggestion.description
    emitUpdate()
  }
}

const clearCustomLocation = () => {
  selectedCustomLocation.value = null
  locationSearchValue.value = ''
  locationSuggestions.value = []
  showLocationSuggestions.value = false
  selectedDistanceBand.value = ''
  emitUpdate()
}

// Initialize from route query
onMounted(async () => {
  const distanceQuery = route.query.distance_band as string
  const neighborhoodsQuery = route.query.neighborhoods as string
  const customLocationQuery = route.query.custom_location as string
  
  if (distanceQuery) {
    selectedDistanceBand.value = distanceQuery
  }
  
  if (neighborhoodsQuery) {
    selectedNeighborhoods.value = neighborhoodsQuery.split(',').filter(Boolean)
  }

  if (customLocationQuery) {
    try {
      selectedCustomLocation.value = JSON.parse(customLocationQuery)
      locationSearchValue.value = selectedCustomLocation.value.description
    } catch (error) {
      console.warn('Failed to parse custom location from query:', error)
    }
  }

  // Initialize Google Places API
  await loadGooglePlacesAPI()
})

// Watch route changes
watch([
  () => route.query.distance_band,
  () => route.query.neighborhoods
], ([newDistance, newNeighborhoods]) => {
  selectedDistanceBand.value = (newDistance as string) || ''
  selectedNeighborhoods.value = typeof newNeighborhoods === 'string' 
    ? newNeighborhoods.split(',').filter(Boolean) 
    : []
})

const selectDistanceBand = (band: string) => {
  selectedDistanceBand.value = selectedDistanceBand.value === band ? '' : band
  emitUpdate()
}

const toggleNeighborhood = (neighborhood: string) => {
  const index = selectedNeighborhoods.value.indexOf(neighborhood)
  if (index > -1) {
    selectedNeighborhoods.value.splice(index, 1)
  } else {
    selectedNeighborhoods.value.push(neighborhood)
  }
  emitUpdate()
}

const emitUpdate = () => {
  emit('update', {
    distanceBand: selectedDistanceBand.value,
    neighborhoods: selectedNeighborhoods.value,
    customLocation: selectedCustomLocation.value
  })
}

const getDistanceBandLabel = (band: string) => {
  const found = distanceBands.find(b => b.value === band)
  return found ? found.label : 'Any distance'
}
</script>

<style scoped>
.location-filters {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-label {
  font-weight: 600;
  font-size: 14px;
  color: var(--black);
}

.distance-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.distance-chip {
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--white);
  user-select: none;
}

.distance-chip:hover {
  border-color: #1985F9;
  background-color: rgba(25, 133, 249, 0.05);
}

.distance-chip.selected {
  background-color: #1985F9;
  border-color: #1985F9;
  color: white;
}

.neighborhoods-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.neighborhood-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.neighborhood-checkbox {
  margin: 0;
}

.neighborhood-label {
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
}

.hotel-count {
  font-size: 12px;
  color: var(--dark-grey);
}

.toggle-neighborhoods {
  background: none;
  border: none;
  color: #1985F9;
  cursor: pointer;
  font-size: 12px;
  padding: 4px 0;
  text-decoration: underline;
}

.filter-info {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--dark-grey);
}

.info-icon {
  font-size: 14px;
}

/* Custom Location Input Styles */
.custom-location-input {
  position: relative;
}

.location-search-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background: var(--white);
}

.location-search-input:focus {
  outline: none;
  border-color: #1985F9;
  box-shadow: 0 0 0 3px rgba(25, 133, 249, 0.1);
}

.location-search-input::placeholder {
  color: #999;
}

.location-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--white);
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.location-suggestion {
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.location-suggestion:hover {
  background-color: #f8f9fa;
}

.location-suggestion:last-child {
  border-bottom: none;
}

.suggestion-icon {
  font-size: 14px;
  color: var(--dark-grey);
}

.suggestion-content {
  flex: 1;
}

.suggestion-name {
  font-size: 14px;
  color: var(--black);
}

.selected-location {
  background-color: #e8f4fd;
  border: 1px solid #1985F9;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.location-icon {
  font-size: 16px;
  color: #1985F9;
}

.location-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.location-name {
  font-size: 14px;
  color: var(--black);
  font-weight: 500;
}

.clear-location-btn {
  background: none;
  border: none;
  color: #1985F9;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  padding: 0;
}

.clear-location-btn:hover {
  color: #0d5cb5;
}
</style>